import { PERMISSIONS, WORKFLOW_INBOX_PERMISSIONS } from '../constants/permissions';
import { routes } from '../constants/routes';
import type { NotificationLinkedEntity } from '../types/notification';
import { hasAnyPermission, hasPermission } from './permissions';

export type NotificationDestination = {
  route:
    | typeof routes.leaveDetails
    | typeof routes.leaveApproval
    | typeof routes.wfhDetails
    | typeof routes.wfhApproval
    | typeof routes.timePermissionDetails
    | typeof routes.attendanceCorrectionDetails
    | typeof routes.attendanceCorrectionApproval
    | typeof routes.workflowDetails
    | typeof routes.attendanceDetails
    | typeof routes.officeCalendar;
  params: Record<string, number | string>;
};

/**
 * Map trusted linked-entity values to app screens.
 * Mounted notification APIs currently return no linked-entity fields,
 * so callers usually pass null and get no destination.
 */
export function getNotificationDestination(
  linkedEntity: NotificationLinkedEntity | null | undefined,
  permissions: readonly string[],
  options?: {
    /** Never reuse notificationId as the linked entity id. */
    notificationId?: number;
  },
): NotificationDestination | null {
  if (!linkedEntity) return null;

  const entityType =
    typeof linkedEntity.entityType === 'string'
      ? linkedEntity.entityType.trim().toUpperCase()
      : '';
  if (!entityType) return null;

  const entityId =
    typeof linkedEntity.entityId === 'number' &&
    Number.isInteger(linkedEntity.entityId) &&
    linkedEntity.entityId > 0
      ? linkedEntity.entityId
      : null;

  const workflowInstanceId =
    typeof linkedEntity.workflowInstanceId === 'number' &&
    Number.isInteger(linkedEntity.workflowInstanceId) &&
    linkedEntity.workflowInstanceId > 0
      ? linkedEntity.workflowInstanceId
      : null;

  // Never treat the notification id as the business entity id.
  if (
    options?.notificationId != null &&
    entityId === options.notificationId &&
    entityType !== 'WORKFLOW'
  ) {
    return null;
  }

  if (
    entityType === 'LEAVE_APPLICATION' ||
    entityType === 'LEAVE_REQUEST' ||
    entityType.startsWith('LEAVE_APPLICATION_')
  ) {
    if (
      workflowInstanceId &&
      hasPermission(permissions, PERMISSIONS.LEAVE_APPROVE)
    ) {
      return {
        route: routes.leaveApproval,
        params: { workflowInstanceId },
      };
    }
    if (!entityId) return null;
    if (
      !hasAnyPermission(permissions, [
        PERMISSIONS.LEAVE_APPLY,
        PERMISSIONS.LEAVE_APPROVE,
      ])
    ) {
      return null;
    }
    return {
      route: routes.leaveDetails,
      params: { leaveApplicationId: entityId },
    };
  }

  if (
    entityType === 'WFH_REQUEST' ||
    entityType === 'WFH' ||
    entityType.startsWith('WFH_REQUEST_')
  ) {
    if (
      workflowInstanceId &&
      hasPermission(permissions, PERMISSIONS.WFH_APPROVE)
    ) {
      return {
        route: routes.wfhApproval,
        params: { workflowInstanceId },
      };
    }
    if (!entityId) return null;
    if (
      !hasAnyPermission(permissions, [
        PERMISSIONS.WFH_APPLY,
        PERMISSIONS.WFH_APPROVE,
      ])
    ) {
      return null;
    }
    return {
      route: routes.wfhDetails,
      params: { wfhRequestId: entityId },
    };
  }

  if (
    entityType === 'TIME_PERMISSION_REQUEST' ||
    entityType === 'PERMISSION_REQUEST' ||
    entityType === 'PERMISSION'
  ) {
    if (!entityId) return null;
    if (!hasPermission(permissions, PERMISSIONS.TIME_PERMISSION_VIEW)) {
      return null;
    }
    return {
      route: routes.timePermissionDetails,
      params: { permissionRequestId: entityId },
    };
  }

  if (
    entityType === 'ATTENDANCE_CORRECTION' ||
    entityType.startsWith('ATTENDANCE_CORRECTION_')
  ) {
    if (
      workflowInstanceId &&
      hasPermission(permissions, PERMISSIONS.ATTENDANCE_APPROVE)
    ) {
      return {
        route: routes.attendanceCorrectionApproval,
        params: { workflowInstanceId },
      };
    }
    if (!entityId) return null;
    if (
      !hasAnyPermission(permissions, [
        PERMISSIONS.ATTENDANCE_VIEW,
        PERMISSIONS.ATTENDANCE_APPROVE,
      ])
    ) {
      return null;
    }
    return {
      route: routes.attendanceCorrectionDetails,
      params: { correctionRequestId: entityId },
    };
  }

  if (entityType === 'WORKFLOW' || entityType === 'WORKFLOW_INSTANCE') {
    const id = workflowInstanceId ?? entityId;
    if (!id) return null;
    if (!hasAnyPermission(permissions, WORKFLOW_INBOX_PERMISSIONS)) return null;
    return {
      route: routes.workflowDetails,
      params: { workflowInstanceId: id },
    };
  }

  if (entityType === 'HOLIDAY') {
    if (!hasPermission(permissions, PERMISSIONS.ATTENDANCE_VIEW)) return null;
    const holidayCalendarId =
      typeof linkedEntity.holidayCalendarId === 'number' &&
      Number.isInteger(linkedEntity.holidayCalendarId) &&
      linkedEntity.holidayCalendarId > 0
        ? linkedEntity.holidayCalendarId
        : null;
    return {
      route: routes.officeCalendar,
      params: holidayCalendarId ? { holidayCalendarId } : {},
    };
  }

  if (entityType === 'ATTENDANCE') {
    // Attendance details require YYYY-MM-DD, not a numeric entity id.
    return null;
  }

  if (entityType === 'EMPLOYEE') {
    if (!entityId) return null;
    if (!hasPermission(permissions, PERMISSIONS.EMPLOYEE_VIEW)) return null;
    return null;
  }

  return null;
}
